import { InventoryFacility } from '@/models/inventory';
import { Order, OrderComment, OrderReturn } from '@/models/order';
import { Tax } from '@/models/tax';

import { OrderState } from './state';

/**
 * A boolean indicating if orders received are loading.
 * TODO: rename to FETCHING_ORDERS_RECEIVED
 * @param value - the fetching/loading state for orders received
 */
export const FETCHING_ORDERS = (state: OrderState, value: boolean) => {
  state.isLoadingOrders = value;
};

/**
 * Saves a list of orders received to the order state.
 * TODO: rename to SET_ORDERS_RECEIVED
 * @param orders - a list of orders received
 */
export const SET_ORDERS = (state: OrderState, orders: Order[]) => {
  state.orders = orders;
};

/**
 * Save an order received to the order state; replaces the order in state if it already exists.
 * TODO: rename to SET_ORDER_RECEIVED
 * @param orders - an order received
 */
export const SET_ORDER = (state: OrderState, order: Order) => {
  const orderIndex = state.orders.findIndex((o) => o.uuid === order.uuid);

  if (orderIndex !== -1) {
    state.orders[orderIndex] = order;
  } else {
    // place at the front of the list because getOrders will return new
    // orders at the front of the list
    state.orders.unshift(order);
  }
};

/**
 * Removes an order received from the order state.
 * TODO: rename to REMOVE_ORDER_RECEIVED
 * @param order - an order received
 */
export const REMOVE_ORDER = (state: OrderState, order: Order) => {
  const orderIndex = state.orders.findIndex((o) => o.uuid === order.uuid);

  state.orders.splice(orderIndex, 1);
};

/**
 * Adds a tax to an Order Received
 * TODO: rename to ADD_TAX_TO_ORDER_RECEIVED
 * @param tax - The tax to add to an Order Received
 */
export const ADD_TAX = (state: OrderState, tax: Tax) => {
  const order = state.orders.find((o) => o.uuid === tax.order);

  if (order) {
    order.taxes.push(tax);
  }
};

/**
 * Removes a tax from an Order Received
 * TODO: rename to REMOVE_TAX_FROM_ORDER_RECEIVED
 * @param tax - The tax to remove from an Order Received
 */
export const REMOVE_TAX = (state: OrderState, tax: Tax) => {
  const order = state.orders.find((o) => o.uuid === tax.order);

  if (order) {
    const taxIndex = order.taxes.findIndex((t) => t.id === tax.id);

    order.taxes.splice(taxIndex, 1);
  }
};

/**
 * Updates a tax on an Order Received
 * TODO: rename to UPDATE_TAX_ON_ORDER_RECEIVED
 * @param tax - The tax to update
 */
export const UPDATE_TAX = (state: OrderState, tax: Tax) => {
  const order = state.orders.find((o) => o.uuid === tax.order);

  if (order) {
    const taxIndex = order.taxes.findIndex((t) => t.id === tax.id);

    order.taxes[taxIndex] = tax;
  }
};

export const LOADING_ORDER_RETURNS = (state: OrderState, value: boolean) => {
  state.isLoadingOrderReturns = value;
};

export const SET_ORDER_RETURNS = (state: OrderState, returns: OrderReturn[]) => {
  state.returns = returns;
};

/**
 * For updating a return, except the "notes" field
 * @param orderReturn - the orderReturn to save
 */
export const SAVE_ORDER_RETURN = (state: OrderState, orderReturn: OrderReturn) => {
  const index = (state.returns || []).findIndex((r) => {
    return r.uuid === orderReturn.uuid;
  });

  if (index >= 0) {
    state.returns[index] = { ...state.returns[index], ...orderReturn };
  }
};

/**
 * For updating the "notes" field in a return
 * @param data - the notes data
 * @param data.uuid - the orderReturn uuid
 * @param data.notes - the notes text
 */
export const UPDATE_RETURN_NOTES = (
  state: OrderState,
  data: {
    uuid: string;
    notes: string;
  },
) => {
  const index = (state.returns || []).findIndex((r) => {
    return r.uuid === data.uuid;
  });

  if (index >= 0) {
    state.returns[index].notes = data.notes;
  }
};

export const SET_PAYMENT_METHODS = (state: OrderState, paymentMethods) => {
  state.paymentMethods = paymentMethods;
};

export const SET_ORDER_COMMENTS = (state: OrderState, comment) => {
  state.orderComments = comment;
};

export const SET_ORDER_DOCUMENTS = (state: OrderState, document) => {
  state.orderDocuments = document;
};

export const SET_SELECTED_FACILITY = (state: OrderState, value: InventoryFacility) => {
  state.selectedFacility = value;
};

/**
 * Adds a comment to the orderComment array
 * @param orderComments
 */
export const PUSH_COMMENT = (state: OrderState, payload: OrderComment) => {
  state.orderComments.unshift(payload);
};
